import {
  BlockChange,
  isBlockWithVariantOverrides,
} from '@basehub/data/replicache/index'
import { clsx } from 'clsx'
import * as React from 'react'
import { OverridesDiffWrapper } from '../../../../../components/diff/overrides-wrapper'
import { hasVariantOverrides } from '@basehub/data/replicache/index'
import { DiffCodeEditor } from '~/sotano-kit/primitives/code-editor'

import s from './input/input.module.scss'
import { IconBlockPreview, ResizableCodeEditorContainer } from './input'
import { VariantPills } from '../variant-pills'

type IconBlockDiffProps = {
  condition: 'removed' | 'added'
  change: BlockChange
  type: 'text' | 'number'
}

export const IconBlockDiff = ({ change, condition }: IconBlockDiffProps) => {
  const isConflictOnValue =
    change.status === 'conflict' && change.conflictPaths.includes('value')
  const beforeBlock = isConflictOnValue ? change.conflicting : change.previous
  const [isResizing, setIsResizing] = React.useState(false)
  const newValue =
    change.current?.type === 'icon' ? change.current?.value ?? '' : ''
  const oldValue = beforeBlock?.type === 'icon' ? beforeBlock?.value ?? '' : ''

  const svg = condition === 'added' ? newValue : oldValue

  return (
    <>
      <div className={clsx(s['container'], 'flex')}>
        <ResizableCodeEditorContainer
          onResizeStart={() => setIsResizing(true)}
          onResizeEnd={() => setIsResizing(false)}
        >
          <DiffCodeEditor
            condition={condition}
            language="html"
            newValue={newValue}
            oldValue={oldValue}
          />
        </ResizableCodeEditorContainer>
        <IconBlockPreview svg={svg} enableControls={!isResizing} readonly />
      </div>
      <IconOverrideDiff change={change} condition={condition} />
    </>
  )
}

type OverrideDiff = {
  current: string
  previous: string
  overrideKey: string
  className?: string
}

export const IconOverrideDiff = ({
  change,
  condition,
}: Omit<IconBlockDiffProps, 'type'>) => {
  const isConflictOnVariants =
    change.status === 'conflict' &&
    change.conflictPaths.includes('variantOverrides')
  const beforeBlock = isConflictOnVariants
    ? change.conflicting
    : change.previous

  const variantsToIterate: OverrideDiff[] = React.useMemo(() => {
    if (!change.current) {
      if (
        condition === 'removed' &&
        beforeBlock &&
        isBlockWithVariantOverrides(beforeBlock)
      ) {
        return Object.entries(beforeBlock.variantOverrides ?? {}).map(
          ([key, override]) => ({
            overrideKey: key,
            current: '',
            previous: String(override.value),
          })
        )
      }
      return []
    }

    if (!hasVariantOverrides(change.current)) return []
    const beforeOverrides =
      beforeBlock && isBlockWithVariantOverrides(beforeBlock)
        ? beforeBlock.variantOverrides
        : {}
    const _overrides = Object.entries(
      change.current.variantOverrides ?? {}
    ).map(([key, override]) => ({
      overrideKey: key,
      current: String(override.value ?? ''),
      previous: String(beforeOverrides?.[key]?.value || ''),
    }))

    if (condition === 'removed') {
      for (const [key, missingOverride] of Object.entries(
        beforeOverrides ?? {}
      )) {
        if (!_overrides.find((o) => key === o.overrideKey)) {
          _overrides.push({
            overrideKey: key,
            current: '',
            previous: String(missingOverride.value ?? ''),
          })
        }
      }
    }

    return _overrides
  }, [beforeBlock, change, condition])

  if (!variantsToIterate.length) return null

  return (
    <OverridesDiffWrapper<OverrideDiff> overrides={variantsToIterate}>
      {variantsToIterate.map((i) => {
        return (
          <React.Fragment key={i.overrideKey + '-frag'}>
            <div className="flex gap-2">
              <VariantPills variants={i.overrideKey} />
            </div>
            <Overrides
              {...i}
              className={clsx('last:!mb-0 mb-3', i.className)}
              condition={condition}
              change={change}
              isConflictOnVariants={isConflictOnVariants}
            />
          </React.Fragment>
        )
      })}
    </OverridesDiffWrapper>
  )
}

const Overrides = (
  props: OverrideDiff & {
    condition: 'added' | 'removed'
    isConflictOnVariants: boolean
    change: BlockChange
  }
) => {
  const [isResizing, setIsResizing] = React.useState(false)
  const isConflictOnVariants =
    props.change.status === 'conflict' &&
    props.change.conflictPaths.includes('variantOverrides')
  const beforeBlock = isConflictOnVariants
    ? props.change.conflicting
    : props.change.previous
  const newValue =
    props.change.current?.type === 'icon'
      ? props.change.current?.value ?? ''
      : ''
  const oldValue = beforeBlock?.type === 'icon' ? beforeBlock?.value ?? '' : ''
  const svg = props.condition === 'added' ? newValue : oldValue

  return (
    <div
      className={clsx(
        s['container'],
        'flex',

        props.condition === 'added' &&
          props.change.status === 'deleted' &&
          'opacity-30',
        'whitespace-pre-wrap',
        props.className
      )}
    >
      <ResizableCodeEditorContainer
        onResizeStart={() => setIsResizing(true)}
        onResizeEnd={() => setIsResizing(false)}
      >
        <DiffCodeEditor
          condition={props.condition}
          language="html"
          newValue={newValue}
          oldValue={oldValue}
        />
      </ResizableCodeEditorContainer>
      <IconBlockPreview svg={svg} enableControls={!isResizing} readonly />
    </div>
  )
}
